'use client';

import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import { MapPin, Users, Bed, Bath, Star, ArrowRight, Loader2 } from 'lucide-react';
import { getApartments, type Apartment } from '@/lib/supabase/apartments';

function formatTZS(amount: number): string {
  return `TZS ${Number(amount).toLocaleString()}`;
}

function getCardImage(apt: Apartment): string {
  if (apt.apartment_images && apt.apartment_images.length > 0) {
    const primary = apt.apartment_images.find((i) => i.is_primary);
    return primary?.url || apt.apartment_images[0].url;
  }
  return apt.cover_image_url || '/assets/images/no_image.png';
}

export default function FeaturedApartments() {
  const [apartments, setApartments] = useState<Apartment[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Always fetch all apartments — getApartments handles featured fallback internally
    getApartments(false)
      .then((all) => {
        // Show featured first, then fill up to 4 total
        const featured = all.filter((a) => a.is_featured);
        const nonFeatured = all.filter((a) => !a.is_featured);
        const display = featured.length > 0
          ? [...featured, ...nonFeatured].slice(0, 4)
          : all.slice(0, 4);
        setApartments(display);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  return (
    <section className="py-28 bg-white" id="apartments">
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-12">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between gap-6 mb-16">
          <div>
            <p className="section-label mb-3">Featured Residences</p>
            <div className="w-10 h-px mb-5" style={{ background: '#C9A96E' }} />
            <h2
              className="text-[#0B1628] mb-4"
              style={{
                fontFamily: "Georgia, 'Times New Roman', serif",
                fontSize: 'clamp(1.75rem, 3vw, 2.75rem)',
                fontWeight: 400,
                lineHeight: 1.15,
                letterSpacing: '-0.01em',
              }}
            >
              Handpicked Apartments<br />
              <span style={{ color: '#C9A96E' }}>Just for You</span>
            </h2>
            <p className="text-[#6B6560] max-w-lg leading-relaxed text-sm">
              Each apartment is professionally furnished and managed to ensure your comfort and satisfaction.
            </p>
          </div>
          <Link
            href="/apartments-listing"
            className="inline-flex items-center gap-2 text-xs font-semibold text-[#0B1628] hover:text-[#C9A96E] transition-colors tracking-widest uppercase whitespace-nowrap shrink-0"
            style={{ letterSpacing: '0.14em' }}
          >
            View All Apartments <ArrowRight size={13} />
          </Link>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="animate-spin text-[#C9A96E]" size={32} />
          </div>
        ) : apartments.length === 0 ? (
          <div className="text-center py-24 text-[#6B6560]">
            <p>No featured apartments available yet.</p>
            <Link href="/apartments-listing" className="btn-primary mt-6 inline-flex">Browse All</Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
            {apartments.map((apt) => (
              <LuxuryCard key={apt.id} apt={apt} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function LuxuryCard({ apt }: { apt: Apartment }) {
  const imgSrc = getCardImage(apt);

  return (
    <div
      className="bg-white overflow-hidden group card-hover flex flex-col"
      style={{
        border: '1px solid #E2DDD6',
        borderRadius: '2px',
        boxShadow: '0 2px 12px rgba(11,22,40,0.05)',
      }}
    >
      {/* Image with zoom */}
      <div className="relative h-60 overflow-hidden img-zoom-hover">
        <AppImage
          src={imgSrc}
          alt={apt.name}
          fill
          className="object-cover transition-transform duration-700 group-hover:scale-107"
          sizes="(max-width: 768px) 100vw, (max-width: 1280px) 50vw, 25vw"
        />
        {/* Gradient overlay */}
        <div
          className="absolute inset-0"
          style={{ background: 'linear-gradient(to top, rgba(5,13,26,0.75) 0%, rgba(5,13,26,0.15) 50%, transparent 100%)' }}
        />

        {/* Tag badge */}
        {apt.tag && (
          <span
            className="absolute top-3 left-3 px-3 py-1 text-xs font-semibold tracking-widest uppercase"
            style={{ background: '#C9A96E', color: '#050D1A', borderRadius: '2px' }}
          >
            {apt.tag}
          </span>
        )}

        {/* Availability badge */}
        <span
          className="absolute top-3 right-3 px-2.5 py-1 text-xs font-semibold tracking-wide uppercase"
          style={{
            background: apt.is_available ? 'rgba(45,106,79,0.88)' : 'rgba(11,22,40,0.80)',
            color: 'white',
            borderRadius: '2px',
            backdropFilter: 'blur(4px)',
          }}
        >
          {apt.is_available ? 'Available' : 'Booked'}
        </span>

        {/* Rating badge */}
        <div
          className="absolute bottom-3 left-3 flex items-center gap-1 px-2.5 py-1"
          style={{
            background: 'rgba(5,13,26,0.65)',
            backdropFilter: 'blur(6px)',
            borderRadius: '2px',
            border: '1px solid rgba(201,169,110,0.25)',
          }}
        >
          <Star size={10} className="text-[#C9A96E] fill-[#C9A96E]" />
          <span className="text-white text-xs font-semibold">5.0</span>
        </div>

        {/* Price overlay on image bottom right */}
        <div className="absolute bottom-3 right-3">
          <p
            className="text-white font-bold tabular-nums"
            style={{
              fontFamily: "Georgia, 'Times New Roman', serif",
              fontSize: '1rem',
              textShadow: '0 1px 4px rgba(0,0,0,0.5)',
            }}
          >
            {formatTZS(Number(apt.price_per_night))}
          </p>
          <p className="text-white/65 text-xs text-right" style={{ fontSize: '0.6rem', letterSpacing: '0.1em' }}>/ NIGHT</p>
        </div>
      </div>

      {/* Content */}
      <div className="p-5 flex flex-col flex-1">
        <h3
          className="font-semibold text-[#0B1628] leading-tight mb-1.5 tracking-wide"
          style={{ fontSize: '0.92rem', letterSpacing: '0.03em' }}
        >
          {apt.name}
        </h3>
        <div className="flex items-center gap-1 text-[#6B6560] text-xs mb-4">
          <MapPin size={10} className="text-[#C9A96E] shrink-0" />
          <span className="truncate">{apt.location}</span>
        </div>

        {/* Features row */}
        <div
          className="flex items-center gap-4 text-xs text-[#6B6560] mb-5 py-3"
          style={{ borderTop: '1px solid #F0EDE8', borderBottom: '1px solid #F0EDE8' }}
        >
          <div className="flex items-center gap-1">
            <Users size={11} className="text-[#C9A96E]" />
            <span>{apt.capacity}</span>
          </div>
          <div className="flex items-center gap-1">
            <Bed size={11} className="text-[#C9A96E]" />
            <span>{apt.bedrooms} bed{apt.bedrooms > 1 ? 's' : ''}</span>
          </div>
          <div className="flex items-center gap-1">
            <Bath size={11} className="text-[#C9A96E]" />
            <span>{apt.bathrooms} bath{apt.bathrooms > 1 ? 's' : ''}</span>
          </div>
        </div>

        {/* CTA buttons */}
        <div className="flex items-center gap-2 mt-auto">
          <Link
            href={`/apartments/${apt.id}`}
            className="flex-1 flex items-center justify-center px-3 py-2.5 text-xs font-semibold tracking-widest uppercase transition-all btn-scale hover:bg-[#F0EDE8]"
            style={{
              border: '1px solid #E2DDD6',
              color: '#0B1628',
              borderRadius: '2px',
              letterSpacing: '0.08em',
            }}
          >
            View
          </Link>
          <Link
            href={`/apartments/${apt.id}`}
            className={`flex-1 flex items-center justify-center px-3 py-2.5 text-xs font-semibold tracking-widest uppercase transition-all btn-scale ${
              apt.is_available
                ? 'hover:brightness-110' :'opacity-50 pointer-events-none'
            }`}
            style={{
              background: apt.is_available ? '#C9A96E' : '#E2DDD6',
              color: apt.is_available ? '#050D1A' : '#6B6560',
              borderRadius: '2px',
              letterSpacing: '0.08em',
            }}
          >
            {apt.is_available ? 'Book Now' : 'Unavailable'}
          </Link>
        </div>
      </div>
    </div>
  );
}